import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { House, Images, Plus, Storefront } from '@phosphor-icons/react';
import { track } from '@/lib/track';

/**
 * Pinterest-style primary navigation.
 * - Desktop (md+): a slim fixed icon rail pinned to the far left (always shown).
 * - Mobile: a fixed bottom tab bar that hides on scroll-down and reappears on
 *   scroll-up (desktop has the space, so the rail never auto-hides).
 * Category tabs stay in the top header — this only owns the app-level
 * destinations (Home, Stories, Create, Spots) plus the Profile module.
 * Saved/bookmarks is no longer a top-level destination — it lives inside the
 * Profile module, and Offline Spots took the slot it used to hold.
 *
 * Route destinations are handled internally via navigate(); modal actions
 * (Create / Profile) come in as callbacks so the parent can open its own
 * overlays. Falls back to navigate('/') when a callback isn't supplied.
 */
const PrimaryNav = ({
  user,
  active = 'home',
  onHome,
  onCreate,
  onProfile,
}) => {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  // Mobile only: hide the bottom bar when scrolling down; reveal on any upward
  // scroll (always shown near the top). Small hysteresis ignores momentum jitter.
  useEffect(() => {
    let lastY = typeof window !== 'undefined' ? window.scrollY : 0;
    const THRESHOLD = 8;
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) {
        setHidden(false);
        lastY = y;
        return;
      }
      const delta = y - lastY;
      if (Math.abs(delta) < THRESHOLD) return; // ignore jitter; keep current state
      setHidden(delta > 0); // down -> hide, up -> show
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Nav destinations are a real navigation signal on their own — which of the
  // four app-level surfaces people actually use was previously unmeasured.
  const go = (path, key) => () => {
    track('nav_click', { nav_item: key, destination: path, is_logged_in: !!user });
    navigate(path);
  };
  const home = () => {
    track('nav_click', { nav_item: 'home', destination: '/', is_logged_in: !!user });
    return onHome ? onHome() : navigate('/');
  };
  const create = () => {
    track('share_fit_click', {
      source: 'primary_nav',
      is_logged_in: !!user,
      outcome: user ? 'opened_form' : 'sent_to_login',
    });
    if (onCreate) return onCreate();
    if (!user) return navigate('/login');
    return navigate('/', { state: { openCreate: true } });
  };
  const profile = () => {
    track('nav_click', { nav_item: 'profile', is_logged_in: !!user });
    if (onProfile) return onProfile();
    return user ? navigate('/', { state: { openProfile: true } }) : navigate('/login');
  };

  // Ordered so Create sits in the visual middle of the mobile bar.
  const items = [
    { key: 'home', label: 'Home', icon: House, onClick: home },
    { key: 'stories', label: 'Stories', icon: Images, onClick: go('/stories', 'stories') },
    { key: 'create', label: 'Share', icon: Plus, onClick: create, primary: true },
    { key: 'spots', label: 'Spots', icon: Storefront, onClick: go('/offline-spots', 'spots') },
  ];

  const Avatar = ({ size = 28 }) =>
    user?.picture ? (
      <img
        src={user.picture}
        alt="Your profile"
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    ) : (
      <span
        className="rounded-full bg-[#2D302A] text-white flex items-center justify-center text-[11px] uppercase"
        style={{ width: size, height: size, fontFamily: 'IBM Plex Mono, monospace' }}
      >
        {(user?.username || '?').charAt(0)}
      </span>
    );

  return (
    <>
      {/* ---------- Desktop: slim left rail (always visible) ---------- */}
      <nav
        className="hidden md:flex fixed left-0 top-0 bottom-0 z-[55] w-[72px] flex-col items-center bg-[#F9F8F6] border-r border-[#E4E1D9] py-5"
        data-testid="primary-nav-rail"
        aria-label="Primary"
      >
        <div className="flex flex-col items-center gap-1.5 flex-1">
          {items.map((it) => (
            <RailButton key={it.key} item={it} active={active === it.key} />
          ))}
        </div>

        {/* Profile pinned to the bottom (logout lives on the profile page) */}
        <button
          type="button"
          onClick={profile}
          title={user ? 'Your profile' : 'Sign in'}
          className={`mt-2 flex items-center justify-center w-11 h-11 rounded-2xl transition-colors ${
            active === 'profile' ? 'bg-[#EAE7DF]' : 'hover:bg-[#EFEDE7]'
          }`}
          data-testid="nav-profile"
        >
          {user ? <Avatar size={30} /> : <ProfileGlyph />}
        </button>
      </nav>

      {/* ---------- Mobile: bottom tab bar ---------- */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-[55] h-[64px] bg-[#F9F8F6]/95 backdrop-blur-xl border-t border-[#E4E1D9] flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ${
          hidden ? 'translate-y-full' : 'translate-y-0'
        }`}
        data-testid="primary-nav-bar"
        aria-label="Primary"
      >
        {items.map((it) => (
          <BarButton key={it.key} item={it} active={active === it.key} />
        ))}

        {/* Profile tab */}
        <button
          type="button"
          onClick={profile}
          className="flex flex-col items-center justify-center flex-1 gap-0.5"
          data-testid="nav-profile"
          aria-label={user ? 'Profile' : 'Sign in'}
        >
          <span
            className={`flex items-center justify-center rounded-full transition-all ${
              active === 'profile' ? 'ring-2 ring-[#2D302A]' : ''
            }`}
          >
            {user ? <Avatar size={24} /> : <ProfileGlyph size={22} />}
          </span>
          <NavLabel active={active === 'profile'}>{user ? 'Profile' : 'Sign in'}</NavLabel>
        </button>
      </nav>
    </>
  );
};

const RailButton = ({ item, active }) => {
  const Icon = item.icon;
  if (item.primary) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        title={item.label}
        aria-label={item.label}
        data-testid={`nav-${item.key}`}
        className="my-1 flex items-center justify-center w-12 h-12 rounded-2xl bg-[#2D302A] text-white shadow-sm hover:bg-[#1A1C18] hover:scale-105 transition-all"
      >
        <Icon size={24} weight="bold" />
      </button>
    );
  }
  const color = active
    ? 'text-[#2D302A] bg-[#EAE7DF]'
    : 'text-[#6B6A68] hover:bg-[#EFEDE7] hover:text-[#2D302A]';
  return (
    <button
      type="button"
      onClick={item.onClick}
      title={item.label}
      aria-label={item.label}
      data-testid={`nav-${item.key}`}
      className={`flex items-center justify-center w-12 h-12 rounded-2xl transition-colors ${color}`}
    >
      <Icon size={24} weight={active ? 'fill' : 'regular'} />
    </button>
  );
};

const BarButton = ({ item, active }) => {
  const Icon = item.icon;
  if (item.primary) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        aria-label={item.label}
        data-testid={`nav-${item.key}`}
        className="flex flex-col items-center justify-center flex-1"
      >
        <span className="flex items-center justify-center w-11 h-11 -mt-5 rounded-full bg-[#2D302A] text-white shadow-lg">
          <Icon size={24} weight="bold" />
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={item.onClick}
      aria-label={item.label}
      data-testid={`nav-${item.key}`}
      className="flex flex-col items-center justify-center flex-1 gap-0.5"
    >
      <Icon
        size={23}
        weight={active ? 'fill' : 'regular'}
        className={active ? 'text-[#2D302A]' : 'text-[#6B6A68]'}
      />
      <NavLabel active={active}>{item.label}</NavLabel>
    </button>
  );
};

const NavLabel = ({ children, active }) => (
  <span
    className={`text-[9px] tracking-[0.05em] uppercase ${
      active ? 'text-[#2D302A]' : 'text-[#6B6A68]'
    }`}
    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
  >
    {children}
  </span>
);

// Simple outline person glyph for logged-out users (avoids pulling another icon
// weight just for this).
const ProfileGlyph = ({ size = 24 }) => (
  <span
    className="flex items-center justify-center rounded-full border border-[#C9C5BA] text-[#6B6A68]"
    style={{ width: size + 6, height: size + 6 }}
  >
    <svg width={size - 6} height={size - 6} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  </span>
);

export default PrimaryNav;
